function createCarbonApi(options = {}) {
    const { apiClient, baseUrl, hostResolver } = options;

    if (!apiClient) {
        throw new Error("createCarbonApi requires apiClient");
    }

    const resolvedBaseUrl = baseUrl ?? (typeof hostResolver === "function" ? hostResolver() : undefined);

    if (!resolvedBaseUrl) {
        throw new Error("createCarbonApi requires baseUrl or hostResolver");
    }

    const xemsHost = typeof hostResolver === "function" ? hostResolver : () => resolvedBaseUrl;

    return {
        /**
         * Carbon Plugin API (v2.5.0)
         * Base URL: /plugins/carbon
         * Vue 2 Compatible
         */
        // ============================================
        // Part 1: 초기 설정 API (42개)
        // ============================================

        // 1. Organization API (5개)
        organization: {
            // 조직 목록 조회
            getOrgs(page, limit) {
                let params = { page, limit };
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/orgs`, { params: filtered });
            },

            // 조직 생성
            createOrg(org) {
                return apiClient.post(xemsHost() + `/plugins/carbon/orgs`, org);
            },

            // 조직 상세 조회
            // v2.6.0: orgIdx → orgCode (자연키 API 전환)
            getOrg(orgCode) {
                return apiClient.get(xemsHost() + `/plugins/carbon/orgs/${orgCode}`);
            },

            // 조직 수정
            // v2.6.0: orgIdx → orgCode (자연키 API 전환)
            updateOrg(orgCode, org) {
                return apiClient.put(xemsHost() + `/plugins/carbon/orgs/${orgCode}`, org);
            },

            // 조직 삭제
            // v2.6.0: orgIdx → orgCode (자연키 API 전환)
            deleteOrg(orgCode) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/orgs/${orgCode}`);
            },

            // 조직 경계 + 운영 경계 통합 저장 (트랜잭션)
            // v2.6.0: orgIdx → orgCode (자연키 API 전환)
            saveBoundarySettings(orgCode, settings) {
                return apiClient.post(xemsHost() + `/plugins/carbon/orgs/${orgCode}/boundary-settings`, settings, {
                    headers: { "Content-Type": "application/json" },
                });
            },
        },

        // 2. Organizational Boundary API (8개)
        orgBoundary: {
            // 조직 경계 목록 조회
            // v2.6.0: orgIdx → orgCode (자연키 API 전환)
            getOrgBoundaries(orgCode, status) {
                let params = { status };
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/orgs/${orgCode}/org-boundaries`, {
                    params: filtered,
                });
            },

            // 조직 경계 생성
            // v2.6.0: orgIdx → orgCode (자연키 API 전환)
            createOrgBoundary(orgCode, boundary) {
                return apiClient.post(xemsHost() + `/plugins/carbon/orgs/${orgCode}/org-boundaries`, boundary);
            },

            // 조직 경계 상세 조회
            getOrgBoundary(boundaryIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/org-boundaries/${boundaryIdx}`);
            },

            // 조직 경계 수정
            updateOrgBoundary(boundaryIdx, boundary) {
                return apiClient.put(xemsHost() + `/plugins/carbon/org-boundaries/${boundaryIdx}`, boundary);
            },

            // 조직 경계 삭제
            deleteOrgBoundary(boundaryIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/org-boundaries/${boundaryIdx}`);
            },

            // 조직 경계 활성화
            activateOrgBoundary(boundaryIdx) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/org-boundaries/${boundaryIdx}/activate`);
            },

            // 조직 경계 상태 변경 (ACTIVE/inactive)
            setOrgBoundaryStatus(boundaryIdx, draftStat) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/org-boundaries/${boundaryIdx}/status`, {
                    draftStat,
                });
            },

            // 시설 매핑 조회
            getFacilities(boundaryIdx) {
                if (boundaryIdx == null || boundaryIdx == undefined) {
                    return Promise.reject(new Error("boundaryIdx is null or undefined"));
                }
                return apiClient.get(xemsHost() + `/plugins/carbon/org-boundaries/${boundaryIdx}/facilities`);
            },

            // 시설 매핑 저장 (트랜잭션)
            saveFacilities(boundaryIdx, facilities) {
                return apiClient.put(
                    xemsHost() + `/plugins/carbon/org-boundaries/${boundaryIdx}/facilities`,
                    facilities,
                );
            },
        },

        // 3. Operational Boundary API (9개)
        operBoundary: {
            // 운영 경계 목록 조회
            // v2.6.0: orgIdx → orgCode (자연키 API 전환)
            getOperBoundaries(orgCode) {
                return apiClient.get(xemsHost() + `/plugins/carbon/orgs/${orgCode}/oper-boundaries`);
            },

            // 운영 경계 생성
            // v2.6.0: orgIdx → orgCode (자연키 API 전환)
            createOperBoundary(orgCode, boundary) {
                return apiClient.post(xemsHost() + `/plugins/carbon/orgs/${orgCode}/oper-boundaries`, boundary);
            },

            // 운영 경계 상세 조회
            getOperBoundary(operBoundaryIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/oper-boundaries/${operBoundaryIdx}`);
            },

            // 운영 경계 수정
            updateOperBoundary(operBoundaryIdx, boundary) {
                return apiClient.put(xemsHost() + `/plugins/carbon/oper-boundaries/${operBoundaryIdx}`, boundary);
            },

            // 운영 경계 삭제
            deleteOperBoundary(operBoundaryIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/oper-boundaries/${operBoundaryIdx}`);
            },

            // 운영 경계 활성화
            activateOperBoundary(operBoundaryIdx) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/oper-boundaries/${operBoundaryIdx}/activate`);
            },

            // 카테고리 설정 조회
            getCategories(operBoundaryIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/oper-boundaries/${operBoundaryIdx}/categories`);
            },

            // 카테고리 설정 저장 (트랜잭션)
            saveCategories(operBoundaryIdx, categories) {
                return apiClient.put(
                    xemsHost() + `/plugins/carbon/oper-boundaries/${operBoundaryIdx}/categories`,
                    categories,
                );
            },

            // 전체 마스터 카테고리 목록 조회
            getAllCategories() {
                return apiClient.get(xemsHost() + `/plugins/carbon/categories`);
            },

            // 운영 경계 상태 변경 (ACTIVE/inactive)
            setOperBoundaryStatus(operBoundaryIdx, draftStat) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/oper-boundaries/${operBoundaryIdx}/status`, {
                    draftStat,
                });
            },
        },

        // 3-1. Category Master API (carbon_categories CRUD)
        category: {
            // 카테고리 목록 조회 (필터는 선택 사항)
            getCategories(params) {
                const filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/categories`, {
                    params: Object.keys(filtered).length ? filtered : undefined,
                });
            },

            // 카테고리 상세 조회 (자연키: categoryCode)
            // GET /plugins/carbon/categories/:categoryCode
            getCategoryByCode(categoryCode) {
                if (categoryCode == null || categoryCode === "") {
                    return Promise.reject(new Error("categoryCode is null or empty"));
                }
                return apiClient.get(xemsHost() + `/plugins/carbon/categories/${encodeURIComponent(categoryCode)}`);
            },

            // 카테고리 생성
            // REQUIREMENTS.md: 필수 필드 scopeType, categoryCode, categoryName
            createCategory(category) {
                return apiClient.post(xemsHost() + `/plugins/carbon/categories`, category);
            },

            // 카테고리 수정
            updateCategory(categoryIdx, category) {
                return apiClient.put(xemsHost() + `/plugins/carbon/categories/${categoryIdx}`, category);
            },

            // 카테고리 삭제
            // 참조 존재 시 409 Conflict 반환 (백엔드 정책)
            deleteCategory(categoryIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/categories/${categoryIdx}`);
            },
        },

        // 4. Access Control API (20개)
        accessControl: {
            // 역할 관리 (5개)
            getRoles(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/admin/roles`, { params: filtered });
            },

            createRole(role) {
                return apiClient.post(xemsHost() + `/plugins/carbon/admin/roles`, role);
            },

            // v2.6.0: roleIdx → roleCode (자연키 API 전환)
            getRole(roleCode) {
                return apiClient.get(xemsHost() + `/plugins/carbon/admin/roles/${roleCode}`);
            },

            // v2.6.0: roleIdx → roleCode (자연키 API 전환)
            updateRole(roleCode, role) {
                return apiClient.put(xemsHost() + `/plugins/carbon/admin/roles/${roleCode}`, role);
            },

            // v2.6.0: roleIdx → roleCode (자연키 API 전환)
            deleteRole(roleCode) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/admin/roles/${roleCode}`);
            },

            // 사용자 관리 (7개)
            getUsers(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/admin/users`, { params: filtered });
            },

            createUser(user) {
                return apiClient.post(xemsHost() + `/plugins/carbon/admin/users`, user);
            },

            // v2.6.0 신규: User Search API (tb_user 검색 + carbon_users 매핑 상태 확인)
            searchUsers(query) {
                return apiClient.get(xemsHost() + `/plugins/carbon/users/search`, { params: { query } });
            },

            // v2.6.0: userIdx → loginId (자연키 API 전환)
            getUser(loginId) {
                return apiClient.get(xemsHost() + `/plugins/carbon/admin/users/${loginId}`);
            },

            // v2.6.0: userIdx → loginId (자연키 API 전환)
            updateUser(loginId, user) {
                return apiClient.put(xemsHost() + `/plugins/carbon/admin/users/${loginId}`, user);
            },

            // v2.6.0: userIdx → loginId (자연키 API 전환)
            deleteUser(loginId) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/admin/users/${loginId}`);
            },

            // v2.6.0: userIdx → loginId (자연키 API 전환)
            getUserRoles(loginId) {
                return apiClient.get(xemsHost() + `/plugins/carbon/admin/users/${loginId}/roles`);
            },

            // v2.6.0: userIdx → loginId (자연키 API 전환)
            assignUserRole(loginId, roleIdx) {
                return apiClient.post(xemsHost() + `/plugins/carbon/admin/users/${loginId}/roles`, { roleIdx });
            },

            // 사용자-역할 매핑 (1개)
            // v2.6.0: userIdx → loginId (자연키 API 전환)
            revokeUserRole(loginId, roleIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/admin/users/${loginId}/roles/${roleIdx}`);
            },

            // 시설 권한 관리 (4개)
            // v2.6.0: userIdx → loginId (자연키 API 전환)
            assignFacilityPermission(loginId, permissions) {
                return apiClient.post(xemsHost() + `/plugins/carbon/admin/users/${loginId}/facilities`, permissions);
            },

            // v2.6.0: userIdx → loginId (자연키 API 전환)
            getFacilityPermissions(loginId) {
                return apiClient.get(xemsHost() + `/plugins/carbon/admin/users/${loginId}/facilities`);
            },

            // v2.6.0: userIdx → loginId (자연키 API 전환). 백엔드 경로는 facilityIdx 사용
            updateFacilityPermission(loginId, facilityIdx, permission) {
                return apiClient.put(
                    xemsHost() + `/plugins/carbon/admin/users/${loginId}/facilities/${facilityIdx}`,
                    permission,
                );
            },

            // v2.6.0: userIdx → loginId (자연키 API 전환). 백엔드 경로는 facilityIdx 사용
            revokeFacilityPermission(loginId, facilityIdx) {
                return apiClient.delete(
                    xemsHost() + `/plugins/carbon/admin/users/${loginId}/facilities/${facilityIdx}`,
                );
            },

            // 내 권한 조회 (4개)
            getMyProfile() {
                return apiClient.get(xemsHost() + `/plugins/carbon/my/profile`);
            },

            getMyRoles() {
                return apiClient.get(xemsHost() + `/plugins/carbon/my/roles`);
            },

            getMyPermissions() {
                return apiClient.get(xemsHost() + `/plugins/carbon/my/permissions`);
            },

            getMyFacilities() {
                return apiClient.get(xemsHost() + `/plugins/carbon/my/facilities`);
            },
        },

        // Utility API (공통 유틸리티)
        utility: {
            // 시설 목록 조회
            /**
             * 시설 목록 또는 단일 시설 조회
             * @param {string|null|undefined} facilityCode - 시설 코드 (지정 시 해당 시설 단건 조회, 미지정 시 목록)
             * @param {Object} [params] - 목록 조회 시 쿼리 (status, facilityType, page, limit)
             */
            getFacilities(facilityCode, params) {
                if (facilityCode != null && facilityCode !== "") {
                    return apiClient.get(xemsHost() + `/plugins/carbon/facilities/${facilityCode}`);
                }
                const filtered = params ? JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined)) : {};
                return apiClient.get(xemsHost() + `/plugins/carbon/facilities`, { params: filtered });
            },

            // 시설 생성
            createFacility(data) {
                return apiClient.post(xemsHost() + `/plugins/carbon/facilities`, data);
            },

            // 시설 수정
            updateFacility(facilityCode, data) {
                return apiClient.put(xemsHost() + `/plugins/carbon/facilities/${facilityCode}`, data);
            },

            // 시설 삭제
            deleteFacility(facilityCode) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/facilities/${facilityCode}`);
            },

            // 시설 담당자 (facilityIdx 기준)
            getFacilityAssignees(facilityIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/facilities/${facilityIdx}/assignees`);
            },
            replaceFacilityAssignees(facilityIdx, body) {
                return apiClient.put(xemsHost() + `/plugins/carbon/facilities/${facilityIdx}/assignees`, body);
            },
            addFacilityAssignee(facilityIdx, body) {
                return apiClient.post(xemsHost() + `/plugins/carbon/facilities/${facilityIdx}/assignees`, body);
            },
            removeFacilityAssignee(facilityIdx, userIdx) {
                return apiClient.delete(
                    xemsHost() + `/plugins/carbon/facilities/${facilityIdx}/assignees/${userIdx}`,
                );
            },
        },

        // ============================================
        // Part 2: 데이터 수집 API (27개)
        // ============================================

        // 5. Activity Data API (10개)
        activityData: {
            // 활동 데이터 목록 조회
            getActivityData(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/activity-data`, { params: filtered });
            },

            // 활동 데이터 생성
            createActivityData(data) {
                return apiClient.post(xemsHost() + `/plugins/carbon/activity-data`, data);
            },

            // 활동 데이터 상세 조회
            getActivityDataDetail(activityIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/activity-data/${activityIdx}`);
            },

            // 활동 데이터 수정
            updateActivityData(activityIdx, data) {
                return apiClient.put(xemsHost() + `/plugins/carbon/activity-data/${activityIdx}`, data);
            },

            // 활동 데이터 삭제
            deleteActivityData(activityIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/activity-data/${activityIdx}`);
            },

            // 활동 데이터 일괄 등록/수정 (트랜잭션)
            // batchUpsertActivityData(dataList) {
            //     return apiClient.post(xemsHost() + `/plugins/carbon/activity-data:batch-upsert`, dataList);
            // },
            batchUpsertActivityData(dataList) {
                return apiClient.post(xemsHost() + `/plugins/carbon/activity-data/batch-upsert`, dataList);
            },

            // 활동 데이터 파일 업로드 (Excel/CSV)
            uploadActivityData(file) {
                const formData = new FormData();
                formData.append("file", file);
                return apiClient.post(xemsHost() + `/plugins/carbon/activity-data/upload`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            },

            // 활동 데이터 템플릿 다운로드 (Excel)
            exportActivityData(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/activity-data/export`, {
                    params: filtered,
                    responseType: "blob",
                });
            },

            // 증빙 파일 업로드 (multipart/form-data)
            // options: { title?, remarks?, fileName? } - 한글 파일명 유지를 위해 fileName 명시 전달 권장
            uploadEvidenceFile(activityIdx, file, options = {}) {
                const formData = new FormData();
                formData.append("file", file);
                if (options.title) formData.append("title", options.title);
                if (options.remarks) formData.append("remarks", options.remarks);
                if (options.fileName) formData.append("fileName", options.fileName);
                // 하위 호환: description → remarks
                if (options.description) formData.append("remarks", options.description);

                return apiClient.post(
                    xemsHost() + `/plugins/carbon/activity-data/${activityIdx}/evidence-files`,
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    },
                );
            },

            // 증빙 파일 목록 조회
            getEvidenceFiles(activityIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/activity-data/${activityIdx}/evidence-files`);
            },

            // 증빙 파일 삭제
            deleteEvidenceFile(activityIdx, fileIdx) {
                return apiClient.delete(
                    xemsHost() + `/plugins/carbon/activity-data/${activityIdx}/evidence-files/${fileIdx}`,
                );
            },
        },

        // 6. Activity Config API (9개) - 신규
        activityConfig: {
            // 수집 설정 생성
            createConfig(config) {
                return apiClient.post(xemsHost() + `/plugins/carbon/activity-configs`, config);
            },

            // 수집 설정 목록 조회
            getConfigs(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/activity-configs`, { params: filtered });
            },

            // 수집 설정 상세 조회
            getConfigDetail(configIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/activity-configs/${configIdx}`);
            },

            // 수집 설정 수정
            updateConfig(configIdx, config) {
                return apiClient.put(xemsHost() + `/plugins/carbon/activity-configs/${configIdx}`, config);
            },

            // 수집 설정 삭제
            deleteConfig(configIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/activity-configs/${configIdx}`);
            },

            // 수집 설정 일괄 생성
            batchCreateConfigs(configs) {
                return apiClient.post(xemsHost() + `/plugins/carbon/activity-configs/batch`, configs);
            },

            // 수집 설정 자동 초기화 (★ 핵심 기능)
            initializeConfigs(params) {
                return apiClient.post(xemsHost() + `/plugins/carbon/activity-configs/initialize`, params);
            },

            // Excel 다운로드
            exportConfigsExcel(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/activity-configs/export`, {
                    params: filtered,
                    responseType: "blob",
                });
            },

            // Excel 업로드
            importConfigsExcel(file) {
                const formData = new FormData();
                formData.append("file", file);

                return apiClient.post(xemsHost() + `/plugins/carbon/activity-configs/import`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            },

            // 배출계수 자동 매칭
            autoMatchFactors(params) {
                return apiClient.post(xemsHost() + `/plugins/carbon/activity-configs/auto-match-factors`, params);
            },

            // 수집 설정 기간 검증 (백엔드 API 없음 - 주석 처리)
            // validatePeriod(params) {
            //     return apiClient.post(xemsHost() + `/plugins/carbon/activity-configs/validate`, params);
            // },
        },

        // 7. Emission Factor API (9개)
        emissionFactor: {
            // 배출계수 목록 조회
            getFactors(params) {
                let filtered;

                if (params) {
                    filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                }

                return apiClient.get(xemsHost() + `/plugins/carbon/emission-factors`, { params: filtered });
            },

            // 배출계수 생성
            createFactor(factor) {
                return apiClient.post(xemsHost() + `/plugins/carbon/emission-factors`, factor);
            },

            // 배출계수 메타데이터 수정 (값 변경은 addFactorValue 사용)
            updateFactor(factorIdx, factor) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/emission-factors/${factorIdx}`, factor);
            },

            // 배출계수 삭제
            deleteFactor(factorIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/emission-factors/${factorIdx}`);
            },

            // 배출계수 단건 조회 (상세 + 값 이력)
            getFactorById(factorIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/emission-factors/${factorIdx}`);
            },

            // ============================================
            // Factor Values (값 타임라인) API
            // ============================================

            // 배출계수 값 이력 조회
            getFactorValues(factorIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/emission-factors/${factorIdx}/values`);
            },

            // 배출계수 값 추가 (새 기간)
            addFactorValue(factorIdx, data) {
                return apiClient.post(xemsHost() + `/plugins/carbon/emission-factors/${factorIdx}/values`, data);
            },

            // 배출계수 값 수정
            updateFactorValue(factorIdx, valueIdx, data) {
                return apiClient.patch(
                    xemsHost() + `/plugins/carbon/emission-factors/${factorIdx}/values/${valueIdx}`,
                    data,
                );
            },

            // 배출계수 값 삭제
            deleteFactorValue(factorIdx, valueIdx) {
                return apiClient.delete(
                    xemsHost() + `/plugins/carbon/emission-factors/${factorIdx}/values/${valueIdx}`,
                );
            },

            // 기간별 유효 배출계수 값 조회
            getFactorValuesByPeriod(params) {
                const filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/factor-values`, {
                    params: filtered,
                });
            },
        },

        // 7-1. GWP Profile API (260311-REDES)
        gwpProfile: {
            getProfiles(params) {
                const filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/gwp-profiles`, { params: filtered });
            },
            getProfile(profileIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/gwp-profiles/${profileIdx}`);
            },
            createProfile(payload) {
                return apiClient.post(xemsHost() + `/plugins/carbon/gwp-profiles`, payload);
            },
            updateProfile(profileIdx, payload) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/gwp-profiles/${profileIdx}`, payload);
            },
            deleteProfile(profileIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/gwp-profiles/${profileIdx}`);
            },
            getValues(profileIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/gwp-profiles/${profileIdx}/values`);
            },
            addValue(profileIdx, payload) {
                return apiClient.post(xemsHost() + `/plugins/carbon/gwp-profiles/${profileIdx}/values`, payload);
            },
            updateValue(profileIdx, gwpValueIdx, payload) {
                return apiClient.patch(
                    xemsHost() + `/plugins/carbon/gwp-profiles/${profileIdx}/values/${gwpValueIdx}`,
                    payload,
                );
            },
            deleteValue(profileIdx, gwpValueIdx) {
                return apiClient.delete(
                    xemsHost() + `/plugins/carbon/gwp-profiles/${profileIdx}/values/${gwpValueIdx}`,
                );
            },
        },

        // ============================================
        // 범용 증빙파일 API (evidence-files)
        // evidenceCategory: ACTIVITY, FACTOR, REPORT, ORG, FACILITY
        // FACTOR의 경우 entityIdx = valueIdx (carbon_factor_values PK)
        // ============================================
        evidenceFile: {
            uploadEvidenceFile(params, file) {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("evidenceCategory", params.evidenceCategory);
                formData.append("entityIdx", params.entityIdx);
                if (params.evidenceType) formData.append("evidenceType", params.evidenceType);
                if (params.title) formData.append("title", params.title);
                if (params.remarks) formData.append("remarks", params.remarks);
                if (params.fileName) formData.append("fileName", params.fileName);
                return apiClient.post(xemsHost() + `/plugins/carbon/evidence-files`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            },
            getEvidenceFiles(params) {
                const filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/evidence-files`, {
                    params: filtered,
                });
            },
            deleteEvidenceFile(fileIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/evidence-files/${fileIdx}`);
            },
        },

        // ============================================
        // Part 3: 계산 & 인벤토리 API (9개)
        // ============================================

        // 8. Calculation API (3개)
        calculation: {
            // 배출량 계산 실행
            runCalculation(params) {
                return apiClient.post(xemsHost() + `/plugins/carbon/calculations/runs`, params);
            },

            // 계산 결과 조회
            getCalculationResult(runIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/calculations/runs/${runIdx}`);
            },

            // 계산 결과 Excel 내보내기
            exportRun(runIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/calculations/runs/${runIdx}/export`, {
                    responseType: "blob",
                });
            },
        },

        // 9. Inventory API (백엔드 routes/inventory.routes.js와 1:1 매핑)
        inventory: {
            // POST /calculations/runs/with-boundaries - 경계 기반 인벤토리 생성 (orgBoundaryIdx, operBoundaryIdx, period 필수, versionIdx 제거됨)
            createInventory(payload) {
                return apiClient.post(xemsHost() + `/plugins/carbon/calculations/runs/with-boundaries`, payload);
            },

            // GET /inventories - 인벤토리 목록 조회 (query: period, orgBoundaryIdx, operBoundaryIdx, stepIdx, page, limit)
            getInventories(params) {
                const filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories`, { params: filtered });
            },

            // GET /workflow-steps/:stepIdx/inventories - 스텝별 인벤토리 목록 조회
            getInventoriesByStep(stepIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/workflow-steps/${stepIdx}/inventories`);
            },

            // GET /inventories/:inventoryIdx - 인벤토리 상세 조회
            getInventory(inventoryIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/${inventoryIdx}`);
            },

            // GET /inventories/lines/:lineIdx - 인벤토리 라인 상세 (GWP, 배출계수 요약·구성요소)
            getInventoryLine(lineIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/lines/${lineIdx}`);
            },

            // DELETE /inventories/:inventoryIdx - 인벤토리 삭제
            deleteInventory(inventoryIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/inventories/${inventoryIdx}`);
            },

            // GET /inventories/trends - 배출 인벤토리 추이 조회 (query: orgBoundaryIdx, operBoundaryIdx, periodFrom, periodTo, groupBy)
            getInventoryTrends(params) {
                const filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/trends`, { params: filtered });
            },

            // GET /inventories/compare - 두 인벤토리 비교 (query: inventory1, inventory2)
            compareInventories(inventory1, inventory2) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/compare`, {
                    params: { inventory1, inventory2 },
                });
            },

            // GET /inventories/:inventoryIdx/quality-check - 데이터 품질 점검
            checkInventoryQuality(inventoryIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/${inventoryIdx}/quality-check`);
            },

            // GET /inventories/:inventoryIdx/uncertainty - 불확실성 평가
            assessUncertainty(inventoryIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/${inventoryIdx}/uncertainty`);
            },

            // GET /inventories/:inventoryIdx/export - 인벤토리 Excel 내보내기
            exportInventory(inventoryIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/${inventoryIdx}/export`, {
                    responseType: "blob",
                });
            },

            // GET /inventories/lines - 월별 인벤토리 라인 목록 조회 (query: orgIdx, facilityCode, categoryCode, periodFrom, periodTo)
            getInventoryLines(params) {
                const filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/lines`, { params: filtered });
            },

            // GET /inventories/annual/lines - 연도별 인벤토리 라인 목록 조회 (query: orgIdx, facilityCode, categoryCode, periodFrom, periodTo)
            getAnnualInventoryLines(params) {
                const filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/annual/lines`, { params: filtered });
            },

            // GET /inventories/annual/:annualInventoryIdx - 연간 인벤토리 상세 조회
            getAnnualInventory(annualInventoryIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventories/annual/${annualInventoryIdx}`);
            },
        },

        // ============================================
        // Part 4: 분석 & 보고 API (27개)
        // ============================================

        // 10. Emission Target API
        target: {
            // 목표 목록 조회
            getTargets(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? ""));
                return apiClient.get(xemsHost() + `/plugins/carbon/emission-targets`, { params: filtered });
            },

            // 목표 생성
            createTarget(target) {
                return apiClient.post(xemsHost() + `/plugins/carbon/emission-targets`, target);
            },

            // 목표 상세 조회
            getTarget(targetIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}`);
            },

            // 목표 수정
            updateTarget(targetIdx, target) {
                return apiClient.put(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}`, target);
            },

            // 기준연도 재계산
            recalculateTarget(targetIdx, payload) {
                return apiClient.put(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}/recalculate`, payload);
            },

            // 목표 삭제
            deleteTarget(targetIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}`);
            },

            // 목표 진행률 조회
            getTargetProgress(targetIdx, params = {}) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}/performance`, {
                    params: filtered,
                });
            },

            // 목표 성과 조회
            getPerformance(targetIdx, params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}/performance`, {
                    params: filtered,
                });
            },
        },

        // 10-1. Dashboard API (탄소배출 대시보드 - orgIdx + year 기준)
        // 서버 규격: year 필수. 연간(YEAR)일 때는 month 미전달, 월간(MONTH)일 때만 month(YYYYMM) 추가.
        dashboard: {
            _params(orgIdx, year, options = {}) {
                const viewMode = options.viewMode || "YEAR";
                const params = { orgIdx, year, viewMode };
                if (viewMode === "MONTH" && options.month) {
                    params.month = options.month; // 연도월 YYYYMM
                }
                return params;
            },
            // 대시보드 개요 (모든 위젯 요약, 초기 로딩 권장)
            // options.noCache === true 이면 캐시 무시하고 강제 갱신
            getOverview(orgIdx, year, options = {}) {
                const params = this._params(orgIdx, year, options);
                if (options.noCache === true) {
                    params.noCache = true;
                }
                return apiClient.get(xemsHost() + `/plugins/carbon/dashboard/overview`, {
                    params,
                });
            },
            // 시설별 배출량
            getByFacility(orgIdx, year, options = {}) {
                const params = this._params(orgIdx, year, options);
                return apiClient.get(xemsHost() + `/plugins/carbon/dashboard/by-facility`, {
                    params,
                });
            },
            // 카테고리별 배출량
            getByCategory(orgIdx, year, options = {}) {
                const params = this._params(orgIdx, year, options);
                return apiClient.get(xemsHost() + `/plugins/carbon/dashboard/by-category`, {
                    params,
                });
            },
            // 주요 배출원 TOP N
            getTopSources(orgIdx, year, limit = 10) {
                const params = { orgIdx, year };
                if (limit != null) params.limit = limit;
                return apiClient.get(xemsHost() + `/plugins/carbon/dashboard/top-sources`, { params });
            },
            // 월별 배출 추이
            getMonthlyTrend(orgIdx, year) {
                return apiClient.get(xemsHost() + `/plugins/carbon/dashboard/monthly-trend`, {
                    params: { orgIdx, year },
                });
            },
            // 인벤토리 현황
            getInventoryStatus(orgIdx, year, options = {}) {
                const params = this._params(orgIdx, year, options);
                return apiClient.get(xemsHost() + `/plugins/carbon/dashboard/inventory-status`, {
                    params,
                });
            },
            // 감축 목표 분석
            getTargetAnalysis(orgIdx, year) {
                return apiClient.get(xemsHost() + `/plugins/carbon/dashboard/target-analysis`, {
                    params: { orgIdx, year },
                });
            },
            // 캐시 강제 갱신 (관리자)
            refreshCache(body = {}) {
                return apiClient.post(xemsHost() + `/plugins/carbon/dashboard/refresh-cache`, body);
            },
        },

        // 10-0. Compliance Standards API (준수기준 관리)
        compliance: {
            // 준수기준 목록 조회
            getCompliances(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/compliance-standards`, { params: filtered });
            },

            // 준수기준 생성
            createCompliance(payload) {
                return apiClient.post(xemsHost() + `/plugins/carbon/compliance-standards`, payload);
            },

            // 준수기준 상세 조회
            getCompliance(complianceIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/compliance-standards/${complianceIdx}`);
            },

            // 준수기준 수정
            updateCompliance(complianceIdx, payload) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/compliance-standards/${complianceIdx}`, payload);
            },

            // 준수기준 삭제
            deleteCompliance(complianceIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/compliance-standards/${complianceIdx}`);
            },
        },

        // 10-2. Annual Implementation Plan API (연도별 이행계획 - Pathways)
        annualPlan: {
            // 연도별 이행계획 목록 조회 (배출 목표별)
            getAnnualPlans(targetIdx, params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                if (targetIdx) {
                    return apiClient.get(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}/pathways`, {
                        params: filtered,
                    });
                } else {
                    // 전체 조회
                    return apiClient.get(xemsHost() + `/plugins/carbon/pathways`, {
                        params: filtered,
                    });
                }
            },

            // 연도별 이행계획 생성
            createAnnualPlan(targetIdx, payload) {
                return apiClient.post(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}/pathways`, payload);
            },

            // 연도별 이행계획 상세 조회
            getAnnualPlan(pathwayIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/pathways/${pathwayIdx}`);
            },

            // 연도별 이행계획 수정
            updateAnnualPlan(targetIdx, pathwayIdx, payload) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/pathways/${pathwayIdx}`, payload);
            },

            // 연도별 이행계획 삭제
            deleteAnnualPlan(targetIdx, pathwayIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/pathways/${pathwayIdx}`);
            },
        },

        // 10-3. Departmental Implementation Plan API (부서별 이행계획 - Org Targets)
        departmentalPlan: {
            // 부서별 이행계획 목록 조회 (연도별 이행계획별 또는 전체)
            getDepartmentalPlans(pathwayIdx, params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                if (pathwayIdx) {
                    return apiClient.get(xemsHost() + `/plugins/carbon/pathways/${pathwayIdx}/org-targets`, {
                        params: filtered,
                    });
                } else {
                    // 전체 조회
                    return apiClient.get(xemsHost() + `/plugins/carbon/org-targets`, {
                        params: filtered,
                    });
                }
            },

            // 부서별 이행계획 생성
            createDepartmentalPlan(pathwayIdx, payload) {
                if (pathwayIdx) {
                    return apiClient.post(xemsHost() + `/plugins/carbon/pathways/${pathwayIdx}/org-targets`, payload);
                } else {
                    // pathwayIdx 없이 직접 생성
                    return apiClient.post(xemsHost() + `/plugins/carbon/org-targets`, payload);
                }
            },

            // 부서별 이행계획 상세 조회
            getDepartmentalPlan(orgTargetIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/org-targets/${orgTargetIdx}`);
            },

            // 부서별 이행계획 수정
            updateDepartmentalPlan(pathwayIdx, orgTargetIdx, payload) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/org-targets/${orgTargetIdx}`, payload);
            },

            // 부서별 이행계획 삭제
            deleteDepartmentalPlan(pathwayIdx, orgTargetIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/org-targets/${orgTargetIdx}`);
            },
        },

        // 10-4. Implementation Actions API (이행 세부항목 - Org Target Items)
        implementationAction: {
            // 이행 세부항목 목록 조회 (부서별 이행계획별 또는 전체)
            getActions(params) {
                let filtered = JSON.parse(JSON.stringify(params || {}, (key, value) => value ?? undefined));
                // params에 orgTargetIdx가 있으면 해당 부서별 이행계획의 항목만 조회
                if (filtered.orgTargetIdx) {
                    const orgTargetIdx = filtered.orgTargetIdx;
                    delete filtered.orgTargetIdx;
                    return apiClient.get(xemsHost() + `/plugins/carbon/org-targets/${orgTargetIdx}/items`, {
                        params: filtered,
                    });
                } else {
                    // 전체 조회
                    return apiClient.get(xemsHost() + `/plugins/carbon/org-target-items`, {
                        params: filtered,
                    });
                }
            },

            // 이행 세부항목 생성
            createAction(payload) {
                const orgTargetIdx = payload.orgTargetIdx;
                if (!orgTargetIdx) {
                    throw new Error("orgTargetIdx is required");
                }
                // orgTargetIdx를 제외한 나머지 필드만 전달
                const { orgTargetIdx: _orgTargetIdx, ...restPayload } = payload;
                // eslint-disable-next-line no-unused-vars
                void _orgTargetIdx; // 사용하지 않는 변수 명시적 처리
                return apiClient.post(xemsHost() + `/plugins/carbon/org-targets/${orgTargetIdx}/items`, restPayload);
            },

            // 이행 세부항목 상세 조회
            getAction(itemIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/org-target-items/${itemIdx}`);
            },

            // 이행 세부항목 수정
            updateAction(itemIdx, payload) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/org-target-items/${itemIdx}`, payload);
            },

            // 이행 세부항목 삭제
            deleteAction(itemIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/org-target-items/${itemIdx}`);
            },
        },

        // 10-5. Goals API (목표 관리 - Emission Targets)
        goal: {
            // 목표 목록 조회
            getGoals(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/emission-targets`, { params: filtered });
            },

            // 목표 생성
            createGoal(payload) {
                return apiClient.post(xemsHost() + `/plugins/carbon/emission-targets`, payload);
            },

            // 목표 상세 조회
            getGoal(targetIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}`);
            },

            // 목표 수정
            updateGoal(targetIdx, payload) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}`, payload);
            },

            // 목표 삭제
            deleteGoal(targetIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/emission-targets/${targetIdx}`);
            },
        },

        // 10-1. Reduction Target API (/targets)
        reductionTarget: {
            getTargets(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/targets`, { params: filtered });
            },

            createTarget(payload) {
                return apiClient.post(xemsHost() + `/plugins/carbon/targets`, payload);
            },

            getPathways(targetIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/targets/${targetIdx}/pathways`);
            },

            createPathway(targetIdx, payload) {
                return apiClient.post(xemsHost() + `/plugins/carbon/targets/${targetIdx}/pathways`, payload);
            },

            deletePathway(targetIdx, pathwayIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/targets/${targetIdx}/pathways/${pathwayIdx}`);
            },

            getSbtiCompliance(targetIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/targets/${targetIdx}/sbti`);
            },

            updateSbtiCompliance(targetIdx, payload) {
                return apiClient.put(xemsHost() + `/plugins/carbon/targets/${targetIdx}/sbti`, payload);
            },

            // 목표 성과 조회 (별칭)
            getTargetPerformance(targetIdx, params = {}) {
                return this.getTargetProgress(targetIdx, params);
            },
        },

        // 11. Project API (5개)
        project: {
            // 감축 프로젝트 목록 조회
            getProjects(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/abatement-projects`, { params: filtered });
            },

            // 감축 프로젝트 생성
            createProject(project) {
                return apiClient.post(xemsHost() + `/plugins/carbon/abatement-projects`, project);
            },

            // 감축 프로젝트 상세 조회
            getProject(projectIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/abatement-projects/${projectIdx}`);
            },

            // 감축 프로젝트 수정
            updateProject(projectIdx, project) {
                return apiClient.put(xemsHost() + `/plugins/carbon/abatement-projects/${projectIdx}`, project);
            },

            // 감축 프로젝트 삭제
            deleteProject(projectIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/abatement-projects/${projectIdx}`);
            },
        },

        // 12. Report API
        report: {
            // 보고서 목록 조회 (query: period, reportType, workflowIdx)
            // params: { period?, reportType?, workflowIdx? }
            getReports(params) {
                const filtered = JSON.parse(JSON.stringify(params || {} || {}, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/reports`, { params: filtered });
            },

            // 보고서 단건 조회
            getReportById(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}`);
            },

            // 보고서 생성 (body: workflowIdx, reportType, format?, reportOptions?, language?)
            createReport(body) {
                return apiClient.post(xemsHost() + `/plugins/carbon/reports`, body);
            },

            // 보고서 생성 (별칭)
            // body: { workflowIdx(필수), reportType, format, language, reportOptions }
            generateReport(params) {
                return this.createReport(params);
            },

            // 보고서 단건 조회
            getReport(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}`);
            },

            // 보고서 수정 (body: title?, reportStat?)
            updateReport(reportIdx, body) {
                return apiClient.put(xemsHost() + `/plugins/carbon/reports/${reportIdx}`, body);
            },

            // 보고서 삭제
            deleteReport(reportIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/reports/${reportIdx}`);
            },

            // 보고서 산출물 목록 조회
            getReportArtifacts(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}/artifacts`);
            },

            // PDF 보고서 PDF 다운로드
            downloadReport(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}/download`, {
                    responseType: "blob",
                });
            },

            // 증빙 ZIP 패키지 다운로드
            downloadPackage(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}/package`, {
                    responseType: "blob",
                });
            },

            // 표지 썸네일 이미지 조회 (PNG blob)
            getThumbnail(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}/thumbnail`, {
                    responseType: "blob",
                });
            },

            // 2x8 미리보기 이미지 조회 (PNG blob)
            getPreview(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}/preview`, {
                    responseType: "blob",
                });
            },

            // 보고서 산출물 목록 조회 (PDF, ZIP 아티팩트)
            getArtifacts(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}/artifacts`);
            },

            // 썸네일 이미지 src URL 헬퍼 (<img :src> 용도)
            thumbnailSrc(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}/thumbnail`);
            },

            // 미리보기 이미지 src URL 헬퍼 (<img :src> 용도)
            previewSrc(reportIdx) {
                return xemsHost() + `/plugins/carbon/reports/${reportIdx}/preview`;
            },

            // 보고서 썸네일 URL (img src용)
            getReportThumbnailUrl(reportIdx) {
                return xemsHost() + `/plugins/carbon/reports/${reportIdx}/thumbnail`;
            },

            // 보고서 미리보기 URL (img src용)
            getReportPreviewUrl(reportIdx) {
                return xemsHost() + `/plugins/carbon/reports/${reportIdx}/preview`;
            },

            // 보고서 증빙 패키지(ZIP) 다운로드
            downloadReportPackage(reportIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/reports/${reportIdx}/package`, {
                    responseType: "blob",
                });
            },
        },

        // 13. Workflow API (8개)
        workflow: {
            // 워크플로우 상태 조회 (Legacy)
            getWorkflowStatus(targetType, targetIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/workflows`, {
                    params: { targetType, targetIdx },
                });
            },

            // 워크플로우 실행 (Legacy)
            executeWorkflow(action) {
                return apiClient.post(xemsHost() + `/plugins/carbon/workflows/execute`, action);
            },

            // 승인 요청 생성
            createApproval(approval) {
                return apiClient.post(xemsHost() + `/plugins/carbon/workflows/approvals`, approval);
            },

            // 승인 요청 목록 조회
            getApprovals(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/workflows/approvals`, { params: filtered });
            },

            // 승인 요청 상세 조회
            getApproval(approvalIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/workflows/approvals/${approvalIdx}`);
            },

            // 승인 처리
            approveRequest(approvalIdx, data) {
                return apiClient.post(xemsHost() + `/plugins/carbon/workflows/approvals/${approvalIdx}/approve`, data);
            },

            // 반려 처리
            rejectRequest(approvalIdx, data) {
                return apiClient.post(xemsHost() + `/plugins/carbon/workflows/approvals/${approvalIdx}/reject`, data);
            },

            // 권한 로그 조회
            getPermissionLogs(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/admin/permission-logs`, { params: filtered });
            },

            // 인벤토리 워크플로우 목록 조회
            getInventoryWorkflows(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/inventory-workflows`, { params: filtered });
            },

            // 워크플로우 생성 전 사전 점검
            getPreCheck(params) {
                let filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/inventory-workflows/pre-check`, {
                    params: filtered,
                });
            },

            /** 조직·연도 기준 Pathway 후보 (워크플로 연동용) */
            getAvailablePathways(params) {
                const filtered = JSON.parse(JSON.stringify(params, (key, value) => value ?? undefined));
                return apiClient.get(xemsHost() + `/plugins/carbon/inventory-workflows/pathways/available`, {
                    params: filtered,
                });
            },

            // 인벤토리 워크플로우 상세 조회
            getInventoryWorkflow(workflowIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}`);
            },

            // 인벤토리 워크플로우 현황 조회 (진행률, 상태별 스텝 수)
            getWorkflowOverview(workflowIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/overview`);
            },

            // 인벤토리 워크플로우 생성
            createInventoryWorkflow(workflow) {
                return apiClient.post(xemsHost() + `/plugins/carbon/inventory-workflows`, workflow);
            },

            // 인벤토리 워크플로우 수정
            updateInventoryWorkflow(workflowIdx, workflow) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}`, workflow);
            },

            // 인벤토리 워크플로우 삭제
            deleteInventoryWorkflow(workflowIdx) {
                return apiClient.delete(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}`);
            },

            // 인벤토리 워크플로우 상태 변경
            updateInventoryWorkflowStatus(workflowIdx, data) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/status`, data);
            },

            // 인벤토리 워크플로우 취소
            cancelInventoryWorkflow(workflowIdx) {
                return apiClient.post(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/cancel`);
            },

            // 인벤토리 워크플로우 검증 (CALCULATED → VERIFIED)
            verifyInventoryWorkflow(workflowIdx) {
                return apiClient.post(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/verify`);
            },

            // 인벤토리 워크플로우 확정 (VERIFIED → CONFIRMED)
            // body: { forceConfirm: "CONFIRM" } — EVIDENCE_MISSING 시 강제 확정용
            confirmInventoryWorkflow(workflowIdx, body = {}) {
                return apiClient.post(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/confirm`, body);
            },

            // 인벤토리 워크플로우 재오픈 (VERIFIED/CONFIRMED/CANCELLED → COLLECTING)
            reopenInventoryWorkflow(workflowIdx) {
                return apiClient.post(xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/reopen`);
            },

            // 목표관리제 이행 정산 (이월·차입·상쇄)
            getComplianceContext(workflowIdx) {
                return apiClient.get(
                    xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/compliance/context`,
                );
            },
            getComplianceSettlement(workflowIdx) {
                return apiClient.get(
                    xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/compliance/settlement`,
                );
            },
            buildComplianceSettlementDraft(workflowIdx, body = {}) {
                return apiClient.post(
                    xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/compliance/settlement/draft`,
                    body,
                );
            },
            applyComplianceSettlement(workflowIdx, body = {}) {
                return apiClient.put(
                    xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/compliance/settlement`,
                    body,
                );
            },
            rollbackComplianceSettlement(workflowIdx, body = {}) {
                return apiClient.put(
                    xemsHost() + `/plugins/carbon/inventory-workflows/${workflowIdx}/compliance/settlement/rollback`,
                    body,
                );
            },
            getTargetCarryBalance(targetIdx, params) {
                const filtered = JSON.parse(JSON.stringify(params, (k, v) => (v ?? undefined)));
                return apiClient.get(xemsHost() + `/plugins/carbon/target-plans/${targetIdx}/carry-balance`, {
                    params: filtered,
                });
            },

            // 워크플로우 스텝 상태 변경
            updateWorkflowStepStatus(stepIdx, data) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/workflow-steps/${stepIdx}/status`, data);
            },

            // 워크플로우 스텝 체크포인트 검증 (CALCULATED → VERIFIED)
            // body: { forceConfirm: "CONFIRM" } 시 증빙 누락 강제 검증
            verifyWorkflowStepCheckpoint(stepIdx, body) {
                return apiClient.post(
                    xemsHost() + `/plugins/carbon/workflow-steps/${stepIdx}/verify-checkpoint`,
                    body || undefined,
                );
            },

            // 워크플로우 스텝 체크포인트 재검증 (VERIFIED 상태에서)
            // body: { forceConfirm: "CONFIRM" } 시 증빙 누락 강제 재검증
            reverifyWorkflowStepCheckpoint(stepIdx, body) {
                return apiClient.post(
                    xemsHost() + `/plugins/carbon/workflow-steps/${stepIdx}/reverify-checkpoint`,
                    body || undefined,
                );
            },

            // 워크플로우 스텝 체크포인트 확정 (레거시 별칭 - verify-checkpoint로 연결)
            confirmWorkflowStepCheckpoint(stepIdx) {
                return this.verifyWorkflowStepCheckpoint(stepIdx);
            },
        },

        // Common Code API (공통 코드 - Carbon Plugin 글로벌 객체용)
        commonCode: {
            /**
             * 공통 코드 그룹 목록 조회
             * @param {string} [useYn='Y'] - 사용 여부
             */
            getGroups(useYn = "Y") {
                const params = useYn ? { useYn } : {};
                return apiClient.get(xemsHost() + `/plugins/carbon/common-code/groups`, { params });
            },

            /**
             * 그룹별 공통 코드 목록 조회
             * @param {string} groupCode - 그룹 코드 (예: FUEL_TYPE, PERIOD_STAT)
             * @param {string} [useYn='Y'] - 사용 여부
             */
            getCodesByGroup(groupCode, useYn = "Y") {
                const params = useYn ? { useYn } : {};
                return apiClient.get(xemsHost() + `/plugins/carbon/common-code/groups/${groupCode}/codes`, {
                    params,
                });
            },

            /**
             * 전체 그룹 + 코드 일괄 로드 (CarbonCodes 초기화용)
             * @param {string} [useYn='Y'] - 사용 여부
             * @returns {Promise<Object>} { [groupCode]: [{ value, text, textEn, meta }] }
             */
            async getAllCodes(useYn = "Y") {
                const groupsRes = await this.getGroups(useYn);
                const groups = groupsRes?.data?.data?.groups ?? groupsRes?.data?.groups ?? [];
                const result = {};
                for (const g of groups) {
                    const codeRes = await this.getCodesByGroup(g.groupCode, useYn);
                    const codes = codeRes?.data?.data?.codes ?? codeRes?.data?.codes ?? [];
                    result[g.groupCode] = codes.map((c) => ({
                        value: c.code,
                        text: c.name,
                        textEn: c.nameEn,
                        meta: c.metadata,
                    }));
                }
                return result;
            },
        },

        // 사용자 알림 API
        notification: {
            // 알림 목록 조회 (페이징, unreadOnly)
            getNotifications(params = {}) {
                const filtered = JSON.parse(JSON.stringify(params, (k, v) => (v === "" || v == null ? undefined : v)));
                return apiClient.get(xemsHost() + `/plugins/carbon/notifications`, { params: filtered });
            },
            // 안읽음 알림 카운트
            getUnreadCount() {
                return apiClient.get(xemsHost() + `/plugins/carbon/notifications/unread-count`);
            },
            // 단일 알림 읽음 처리
            markAsRead(notificationIdx) {
                return apiClient.patch(xemsHost() + `/plugins/carbon/notifications/${notificationIdx}/read`);
            },
            // 전체 알림 읽음 처리
            markAllAsRead() {
                return apiClient.patch(xemsHost() + `/plugins/carbon/notifications/read-all`);
            },
        },

        // 감사 로그 (Read-only)
        auditLog: {
            getLogs(params = {}) {
                const filtered = JSON.parse(JSON.stringify(params, (k, v) => (v === "" || v == null ? undefined : v)));
                return apiClient.get(xemsHost() + `/plugins/carbon/audit-logs`, { params: filtered });
            },
            getStatistics(params = {}) {
                const filtered = JSON.parse(JSON.stringify(params, (k, v) => (v === "" || v == null ? undefined : v)));
                return apiClient.get(xemsHost() + `/plugins/carbon/audit-logs/statistics`, { params: filtered });
            },
            getLogByIdx(logIdx) {
                return apiClient.get(xemsHost() + `/plugins/carbon/audit-logs/${logIdx}`);
            },
            getEntityHistory(entity, entityIdx) {
                return apiClient.get(
                    xemsHost() + `/plugins/carbon/audit-logs/entity/${encodeURIComponent(entity)}/${entityIdx}`,
                );
            },
        },
    };
}

export default createCarbonApi;
